import type { SavedCryptoWalletAddress } from '@/entities/crypto-address';
import { Button, Card, UserProfileAction } from '@/shared/components';
import EditIcon from '@/shared/assets/edit-icon.svg?react';
import type { SavedCryptoAddress } from '@/entities/crypto-address';
import {
  BANK_ADDRESS_PARAM_IDS,
  type SavedBankAddress,
} from '@/entities/bank-address';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import type { SavedCard } from '@/entities/card';

const classes = tv({
  slots: {
    container: 'flex flex-col gap-[3.2rem]',
    section: 'flex flex-col gap-[1.2rem]',
    sectionTitle: 'p4 text-secondary uppercase',
    card: 'p-[0.8rem] rounded-[1.6rem] w-full shadow-none flex flex-col gap-0',
    actionItem: 'px-[1.6rem] py-[1.4rem]',
    iconWrapper: 'size-[4.4rem] min-w-[4.4rem]',
    cardIcon: 'w-[4.4rem] h-[4.4rem]',
    title: 'p3 text-primary break-all',
    subtitle: 'p5 text-secondary',
    editButton: 'w-[4.4rem] h-[4.4rem]',
    editIcon: 'size-[2.4rem] text-tertiary group-hover:text-primary',
  },
});

type Props = {
  cryptoAddresses: SavedCryptoWalletAddress[];
  bankAddresses: SavedBankAddress[];
  cards: SavedCard[];
  onEditCryptoAddress: (savedCryptoAddress: SavedCryptoAddress) => void;
  onEditBankAddress: (savedBankAddress: SavedBankAddress) => void;
  onEditCard: (savedCard: SavedCard) => void;
};

export const SavedAddressesContent = ({
  cryptoAddresses,
  bankAddresses,
  cards,
  onEditCryptoAddress,
  onEditBankAddress,
  onEditCard,
}: Props) => {
  const { t } = useTranslation();
  const c = classes();

  const hasCryptoAddresses = cryptoAddresses.length > 0;
  const hasBankAddresses = bankAddresses.length > 0;
  const hasCards = cards.length > 0;

  return (
    <div className={c.container()}>
      {hasCryptoAddresses && (
        <div className={c.section()}>
          <p className={c.sectionTitle()}>
            {t('savedAddresses.cryptoAddress.cardTitle')}
          </p>

          <Card className={c.card()}>
            {cryptoAddresses.map(cryptoAddress => (
              <UserProfileAction
                key={cryptoAddress.templateId}
                icon={
                  <img
                    src={cryptoAddress.networkIcon}
                    alt={cryptoAddress.networkName}
                    className={c.cardIcon()}
                  />
                }
                title={
                  <span className={c.title()}>
                    {cryptoAddress.templateName}
                  </span>
                }
                subtitle={
                  <span className={c.subtitle()}>
                    {cryptoAddress.networkName} {cryptoAddress.networkCode}
                  </span>
                }
                className={c.actionItem()}
                iconWrapperClassName={c.iconWrapper()}
                onClick={() => onEditCryptoAddress(cryptoAddress)}>
                <Button variant="icon" className={c.editButton()}>
                  <EditIcon className={c.editIcon()} />
                </Button>
              </UserProfileAction>
            ))}
          </Card>
        </div>
      )}

      {hasCards && (
        <div className={c.section()}>
          <p className={c.sectionTitle()}>
            {t('savedAddresses.card.cardTitle')}
          </p>

          <Card className={c.card()}>
            {cards.map(card => (
              <UserProfileAction
                key={card.templateId}
                icon={
                  card.cardTypeIcon && (
                    <img
                      src={card.cardTypeIcon}
                      alt={card.cardType}
                      className={c.cardIcon()}
                    />
                  )
                }
                title={<span className={c.title()}>{card.templateName}</span>}
                subtitle={
                  <span className={c.subtitle()}>{card.cardNumber}</span>
                }
                className={c.actionItem()}
                iconWrapperClassName={c.iconWrapper()}
                onClick={() => onEditCard(card)}>
                <Button variant="icon" className={c.editButton()}>
                  <EditIcon className={c.editIcon()} />
                </Button>
              </UserProfileAction>
            ))}
          </Card>
        </div>
      )}

      {hasBankAddresses && (
        <div className={c.section()}>
          <p className={c.sectionTitle()}>
            {t('savedAddresses.bankAddress.cardTitle')}
          </p>

          <Card className={c.card()}>
            {bankAddresses.map(bankAddress => (
              <UserProfileAction
                key={bankAddress.templateId}
                title={
                  <span className={c.title()}>{bankAddress.templateName}</span>
                }
                subtitle={
                  <span className={c.subtitle()}>
                    {
                      bankAddress.params.find(
                        param =>
                          param.paramId === BANK_ADDRESS_PARAM_IDS.bankName
                      )?.paramValue
                    }
                  </span>
                }
                className={c.actionItem()}
                onClick={() => onEditBankAddress(bankAddress)}>
                <Button variant="icon" className={c.editButton()}>
                  <EditIcon className={c.editIcon()} />
                </Button>
              </UserProfileAction>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
};
